import { DeviceInfoResponse } from '../device-info/device-info.response';
import { PermissionInfo } from '../permissions-manager/permissions';
import { GDSTroubleshooterMessage, LocationInfoStatus, LocationInfoStatusOptions } from './location-info';

export type Optional<Type> = Type | undefined | null;

export type OnInitialized<K> = Optional<(initializationStatus?: Optional<K>, locationInfoStatus?: LocationInfoStatus) => void>;
export type OnUninitialized = Optional<() => void>;

export const enum CoordinatorStates {
    NOTINITIALIZED = 'NOTINITIALIZED',
    INITIALIZING = 'INITIALIZING',
    INITIALIZED = 'INITIALIZED',
    ERROR = 'ERROR',
    LOCATIONAVAILABLE = 'LOCATIONAVAILABLE',
    PROBING = 'PROBING',
}

export type PLCIdentifier = 'geocomply' | 'oobeeAndroid' | 'oobeeiOS' | 'gchtml5';

export interface ConfigurationData {
    readonly enableNDBS: boolean;
}

export interface IPLCProbeResult {
    readonly status: boolean;
    readonly additionalInfo?: { [key: string]: any };
}

export type OnCoordinatorProbed = (probeResult: ICoordinatorProbeResult) => void;
export interface ICoordinatorProbeResult extends IPLCProbeResult {
    readonly coordinatorState: CoordinatorStates;
}

export type OnProbed = (probeResult: ICoordinatorProbeResult) => void;

export interface IOnError {
    onError: OnError;
}
export interface IErrorData {
    errorCode: number;
    errorMessage: string;
    errorType?: ErrorTypes;
    additionalInfo?: any;
}
export enum ErrorTypes {
    APP_ERROR = 'APPLICATION_ERROR',
    COMM_ERROR = 'COMMUNICATION_ERROR',
    NETWORK_ERROR = 'NETWORK_ERROR',
    DATA_ERROR = 'DATA_ERROR',
    CONFIG_ERROR = 'CONFIGURATION_ERROR',
    FATAL = 'FATAL',
}
export type OnError = Optional<(errorData: IErrorData) => void>;

export interface IIntegrationApp {
    readonly appDownloadUrl: string;
    readonly configuration: Optional<ConfigurationData>;
    onInitialized: OnInitialized<CoordinatorStates>;
    onError: OnError;
    onProbed: OnProbed;
    onUninitialized: OnUninitialized;
    initialize(input?: IInitializationInput): void;
    probe(input?: IInitializationInput): void;
    uninitialize(): void;
    triggerFetchLocation(input?: IInitializationInput): void;
    locationInfoStatus(infoData: LocationInfoStatusOptions): void;
    handleLocationInfoStatus(
        locationInfoStatus?: LocationInfoStatus,
        notOkCallback?: (permissionsInfo: PermissionInfo) => void,
        okCallback?: () => void,
    ): void;
    readLocationInfoStatus(): Optional<LocationInfoStatus>;
}

export interface IInitializationInput {
    readonly session?: string;
    readonly channelId?: string;
    readonly plcIdentifier?: PLCIdentifier | string;
    readonly showPLCPopup?: boolean;
    readonly productId?: string;
}

export enum RetryType {
    NonRetry = 0,
    Retry = 1,
}

export interface GDSResponse extends DeviceInfoResponse {
    locationStatus: string;
    isLocationAvailable: boolean;
    errorCode: number;
    errorMsg: string;
    troubleShooterMessages?: { tsMsgs?: GDSTroubleshooterMessage[] };
}

export type BootstrapAssets = { [key: string]: BootstrapAsset };

export interface BootstrapAsset {
    readonly url?: string;
    readonly asModule?: boolean;
    readonly scriptBody?: string;
    readonly loadDelay?: number;
    readonly uniqueAlias?: string;
}

export interface AdditionalCoordinatorSettings {
    readonly useVanillaGeolocation?: boolean;
    readonly posApiEndpoint?: string;
    readonly oobeeIntimation?: { readonly enabled?: boolean; readonly toasterId?: string };
    saveDeviceDetails?: boolean; // sports unit tests re-assign, so had to drop readonly :-(
}

export interface ToastMessage {
    readonly message: string;
    readonly errorType: string;
}

export interface InstallerSettings {
    readonly plc?: {
        readonly downloadUrl?: string;
        readonly installerKey?: string;
        readonly version?: string;
    };
    readonly glv?: {
        readonly appStoreIntent?: string;
        readonly playStoreIntent?: string;
    };
    readonly entain?: {
        readonly appStoreIntent?: string;
        readonly playStoreIntent?: string;
    };
}

export interface SynchronizationSettings {
    readonly enabled: boolean;
    readonly events: {
        readonly start: string[];
        readonly stop: string[];
    };
}

export interface InteractiveLaunchSettings {
    readonly displayWidgetOnError: boolean;
    readonly displayWidgetOnErrorCodes: number[];
    readonly displayWidgetOnUninitialize: boolean;
}

export interface NativeSettings {
    readonly continueOnApps: boolean;
    readonly notifyNativeToFetch: boolean;
}

export interface ErrorSettings {
    readonly retryOnError: boolean;
    readonly logFailures: boolean;
}

export type PluginLoadSettings = { [key: string]: string };
